import { Input, Combobox, typographyStyles } from "@newsela/angelou";
import PropTypes from "prop-types";

import { FACULTY_ADDER_ACTIONS } from "@/admin/hooks/useFacultyAdderReducer";
import useSchoolsByRole from "@/admin/hooks/useSchoolsByRole";
import SubjectSelector from "@/components/SubjectSelector";
import {
  inputsGridContainerCss,
  comboboxCss,
} from "../AdminConsoleFacultyAdderUserSection/AdminConsoleFacultyAdderUserSection.style";
import { getInputErrorMessages } from "./AdminConsoleFacultyAdderTeacherSection.utils";

const propTypes = {
  // Component form values.
  formValues: PropTypes.shape({
    lowestGrade: PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          key: PropTypes.number,
          description: PropTypes.string,
        }),
      ]),
      touched: PropTypes.bool,
      errorMessage: PropTypes.string,
    }),
    highestGrade: PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          key: PropTypes.number,
          description: PropTypes.string,
        }),
      ]),
      touched: PropTypes.bool,
      errorMessage: PropTypes.string,
    }),
    sisId: PropTypes.string,
    school: PropTypes.shape({
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
          uniqueId: PropTypes.string,
        }),
      ]),
      touched: PropTypes.bool,
      errorMessage: PropTypes.string,
    }),
    isTeacher: PropTypes.bool,
    subjects: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        checked: PropTypes.bool,
      }),
    ),
  }),
  // Parent reducer dispatcher.
  dispatchAction: PropTypes.func,
};

const GRADES_LIST = [
  { key: 0, description: "K" },
  { key: 1, description: "1" },
  { key: 2, description: "2" },
  { key: 3, description: "3" },
  { key: 4, description: "4" },
  { key: 5, description: "5" },
  { key: 6, description: "6" },
  { key: 7, description: "7" },
  { key: 8, description: "8" },
  { key: 9, description: "9" },
  { key: 10, description: "10" },
  { key: 11, description: "11" },
  { key: 12, description: "12" },
];

const AdminConsoleFacultyAdderTeacherSection = ({
  formValues,
  dispatchAction,
}) => {
  const SCHOOLS_LIST = useSchoolsByRole();
  const [
    lowestGradeError,
    highestGradeError,
    schoolError,
    highestLowerThanLowestError,
  ] = getInputErrorMessages(formValues);

  const handleSubjectsChange = (subjectName, subjectId, subjectCheckValue) => {
    dispatchAction({
      type: FACULTY_ADDER_ACTIONS.SET_ROLE_SECTION,
      payload: {
        ...formValues,
        subjects: formValues.subjects.map((prevS) => {
          if (prevS.id === subjectId) {
            prevS.checked = subjectCheckValue;
          }
          return prevS;
        }),
      },
    });
  };

  return (
    <>
      <div css={inputsGridContainerCss}>
        <div>
          <Combobox
            label="Lowest grade taught"
            items={GRADES_LIST}
            selectedItem={formValues?.lowestGrade?.value}
            itemToString={(item) => item?.description || ""}
            touched={formValues?.lowestGrade?.touched}
            error={lowestGradeError}
            onSelectedItemChange={(e) => {
              dispatchAction({
                type: FACULTY_ADDER_ACTIONS.SET_ROLE_SECTION,
                payload: {
                  ...formValues,
                  lowestGrade: {
                    ...formValues.lowestGrade,
                    value: e.selectedItem,
                    touched: true,
                  },
                },
              });
            }}
            __cssFor={{
              root: comboboxCss,
            }}
          />
        </div>
        <div>
          <Combobox
            label="Highest grade taught"
            items={GRADES_LIST}
            selectedItem={formValues?.highestGrade?.value}
            itemToString={(item) => item?.description || ""}
            touched={formValues?.highestGrade?.touched}
            error={highestGradeError || highestLowerThanLowestError}
            onSelectedItemChange={(e) => {
              dispatchAction({
                type: FACULTY_ADDER_ACTIONS.SET_ROLE_SECTION,
                payload: {
                  ...formValues,
                  highestGrade: {
                    ...formValues.highestGrade,
                    value: e.selectedItem,
                    touched: true,
                  },
                },
              });
            }}
            __cssFor={{
              root: comboboxCss,
            }}
          />
        </div>
        <div>
          <Input
            name="sisId"
            label="SIS ID"
            value={formValues?.sisId}
            maxLength={50}
            onChange={(e) => {
              dispatchAction({
                type: FACULTY_ADDER_ACTIONS.SET_ROLE_SECTION,
                payload: {
                  ...formValues,
                  sisId: e.target.value,
                },
              });
            }}
            isRequired={false}
            __cssFor={{ label: typographyStyles.heading6Obj }}
          />
        </div>
        <div>
          <Combobox
            label="School"
            items={SCHOOLS_LIST}
            selectedItem={formValues?.school?.value}
            itemToString={(item) => item?.name || ""}
            touched={formValues?.school?.touched}
            error={schoolError}
            onSelectedItemChange={(e) => {
              dispatchAction({
                type: FACULTY_ADDER_ACTIONS.SET_ROLE_SECTION,
                payload: {
                  ...formValues,
                  school: {
                    ...formValues.school,
                    value: e.selectedItem,
                    touched: true,
                  },
                },
              });
            }}
            __cssFor={{
              root: comboboxCss,
            }}
          />
        </div>
      </div>

      <SubjectSelector
        title="Subjects taught"
        subjects={formValues?.subjects}
        onSubjectsChange={handleSubjectsChange}
      />
    </>
  );
};

AdminConsoleFacultyAdderTeacherSection.propTypes = propTypes;

export default AdminConsoleFacultyAdderTeacherSection;
